const excludedKeys = ['successSaga', 'errorSaga'];

/**
 * Converts a payload object to a query parameter string and appends it to the base URL.
 * Filters out keys specified in `excludedKeys` and values that are `undefined` or `null`.
 *
 * @template T - The type of the payload object. Defaults to `object`.
 *
 * @param {T} payload - The payload object to convert.
 * @param {string} baseUrl - The base URL to append the query parameters to.
 *
 * @returns {string} - The resulting URL with query parameters.
 *
 * @example
 * ```typescript
 * const payload = { key1: 'value1', key2: 'value2', successSaga: 'shouldBeExcluded' };
 * const baseUrl = 'http://example.com/api';
 * const result = payloadToQueryParamsUrl(payload, baseUrl);
 * console.log(result); // Output: 'http://example.com/api?key1=value1&key2=value2'
 * ```
 */
export const payloadToQueryParamsUrl = <T = object>(payload: T, baseUrl: string) => {
  const filteredPayload = Object.entries(payload as never)
    .filter(([key, value]) => !excludedKeys.includes(key) && value !== undefined && value !== null)
    .reduce((obj: Record<string, string>, [key, value]) => {
      if (key === 'orderBy' && typeof value === 'object' && value !== null) {
        Object.entries(value as Record<string, string>).forEach(([orderKey, orderValue]) => {
          // eslint-disable-next-line no-param-reassign
          obj[`orderBy.${orderKey}`] = `${orderValue}`;
        });
      } else {
        // eslint-disable-next-line no-param-reassign
        obj[key] = `${value}`;
      }
      return obj;
    }, {});

  const query = new URLSearchParams(filteredPayload).toString();
  return query ? `${baseUrl}?${query}` : baseUrl;
};
